import { Box, Stack, Typography, useTheme } from "@mui/material"
import { useTranslation } from "react-i18next"

import { TOWER_GLOBAL_LEAN_COLORS } from "helpers/config/dashboards/tower/defs"

interface TowerPositionFromAboveProps {
  magnitude: number
  heading: number
  hideLegend?: boolean
}

const MARKER = 14

const cx = 60
const cy = 60
const outerR = 72
const RING_COUNT = 3

// Candidate ring steps in decimal degrees (from arcminute-scale leans up to
// several degrees).
const NICE_STEPS = [0.05, 0.1, 0.25, 0.5, 2, 5, 10]

// Smallest step whose RING_COUNT rings cover the magnitude (+5% headroom).
const pickStep = (magnitude: number): number => {
  const target = Math.max(magnitude, 0) * 1.05
  return (
    NICE_STEPS.find((s) => s * RING_COUNT >= target) ?? NICE_STEPS[NICE_STEPS.length - 1]
  )
}

// Compact ring label: degrees (min decimals) at ≥0.1°, arcminutes below that.
const formatRingLabel = (valueDeg: number, step: number): string => {
  if (step < 0.1) return `${Math.round(valueDeg * 60)}'`
  return `${valueDeg.toFixed(2).replace(/\.?0+$/, "")}°`
}

// Counter-clockwise convention: 0°=Front/top, +90°=Left, -90°=Right, 180°=Back
const pointAt = (deg: number, r: number) => {
  const a = (deg * Math.PI) / 180
  return { x: cx - r * Math.sin(a), y: cy - r * Math.cos(a) }
}

const tickAngles = [0, 45, 90, 135, 180, -135, -90, -45]
const diagLabels = [
  { deg: 45, label: "45°", anchor: "end" },
  { deg: 135, label: "135°", anchor: "end" },
  { deg: -135, label: "-135°", anchor: "start" },
  { deg: -45, label: "-45°", anchor: "start" },
]

const cardinals = [
  { deg: 0, nameKey: "FRONT", angleLabel: "0°", anchor: "middle", off: 12 },
  { deg: 90, nameKey: "HEADING_LEFT", angleLabel: "90°", anchor: "end", off: 7 },
  { deg: 180, nameKey: "BACK", angleLabel: "180°", anchor: "middle", off: 12 },
  { deg: -90, nameKey: "HEADING_RIGHT", angleLabel: "-90°", anchor: "start", off: 7 },
]

const TowerPositionFromAbove = ({
  magnitude,
  heading,
  hideLegend = false,
}: TowerPositionFromAboveProps) => {
  const theme = useTheme()
  const { t } = useTranslation()

  const ringColor = theme.palette.neutral[600]
  const labelColor = theme.palette.text.primary
  const axisColor = theme.palette.neutral[600]
  const frontColor = TOWER_GLOBAL_LEAN_COLORS.front
  const rightColor = TOWER_GLOBAL_LEAN_COLORS.right
  const magnitudeColor = TOWER_GLOBAL_LEAN_COLORS.magnitude
  // Reference (0°/plumb) outline
  const baseColor = theme.palette.common.white
  const currentColor = theme.palette.common.white
  const currentFill = theme.palette.background.paper
  const alertColor = theme.palette.error.main
  const labelBg = theme.palette.background.default

  const step = pickStep(magnitude)
  const scaleMaxDeg = step * RING_COUNT
  const k = outerR / scaleMaxDeg

  const rawRadius = k * Math.max(magnitude, 0)
  const offScale = rawRadius > outerR
  const drawnRadius = Math.min(rawRadius, outerR)
  const { x: px, y: py } = pointAt(heading, drawnRadius)

  const diagLabelR = outerR + 8

  const ticks = tickAngles.map((deg) => pointAt(deg, outerR))

  const ringScale = Array.from({ length: RING_COUNT }, (_, i) => {
    const idx = i + 1
    return {
      r: (outerR * idx) / RING_COUNT,
      value: step * idx,
      isOuter: idx === RING_COUNT,
    }
  })

  const legend = [
    { color: frontColor, dashed: true, label: t("plot_titles.FRONT_LEAN") },
    { color: rightColor, dashed: true, label: t("plot_titles.RIGHT_LEAN") },
    { color: magnitudeColor, dashed: false, label: t("plot_titles.MAGNITUDE") },
  ]

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        width: "100%",
        height: "100%",
        minHeight: 0,
      }}
    >
      {/* Fills the available card space; the square viewBox keeps it centered */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <svg
          viewBox="-54 -44 236 210"
          width="100%"
          height="100%"
          preserveAspectRatio="xMidYMid meet"
          style={{
            display: "block",
            maxHeight: "100%",
            maxWidth: "min(100%, 440px)",
          }}
        >
          {/* Dashed radial spokes to every tick (orientation grid, not data) */}
          {tickAngles.map((deg, i) => {
            const p = ticks[i]
            return (
              <line
                key={deg}
                x1={cx}
                y1={cy}
                x2={p.x}
                y2={p.y}
                stroke={axisColor}
                strokeWidth={0.6}
                strokeDasharray="2 3"
                opacity={0.25}
              />
            )
          })}

          {/* Rings — even, quiet weight (radial = lean size) */}
          {ringScale.map(({ r, isOuter }) => (
            <circle
              key={r}
              cx={cx}
              cy={cy}
              r={r}
              fill="none"
              stroke={isOuter && offScale ? alertColor : ringColor}
              strokeWidth={isOuter && offScale ? 1.25 : 1}
              opacity={isOuter && offScale ? 0.7 : 0.2}
            />
          ))}

          {/* Ring degree labels (magnitude scale) — beside each ring, top axis */}
          {ringScale.map(({ r, value }) => {
            const label = formatRingLabel(value, step)
            const ly = cy - r - 2
            const w = label.length * 3 + 2.5
            return (
              <g key={`lbl-${r}`}>
                <rect
                  x={cx + 2.5}
                  y={ly - 4.5}
                  width={w}
                  height={6}
                  rx={1}
                  fill={labelBg}
                  opacity={0.6}
                />
                <text
                  x={cx + 3.5}
                  y={ly}
                  textAnchor="start"
                  dominantBaseline="auto"
                  fill={magnitudeColor}
                  fontSize={5}
                  fontFamily="inherit"
                  fontWeight={400}
                  opacity={0.85}
                  style={{ fontVariantNumeric: "tabular-nums" }}
                >
                  {label}
                </text>
              </g>
            )
          })}

          {/* Diagonal heading labels (angular direction) — outside outer ring */}
          {diagLabels.map(({ deg, label, anchor }) => {
            const pos = pointAt(deg, diagLabelR)
            return (
              <text
                key={deg}
                x={pos.x}
                y={pos.y}
                textAnchor={anchor as "start" | "middle" | "end"}
                dominantBaseline="middle"
                fill={labelColor}
                fontSize={5.5}
                fontFamily="inherit"
                opacity={0.55}
              >
                {label}
              </text>
            )
          })}

          {/* Cardinal labels — single line, e.g. "Front 0°" — outside ring */}
          {cardinals.map(({ deg, nameKey, angleLabel, anchor, off }) => {
            const pos = pointAt(deg, outerR + off)
            return (
              <text
                key={deg}
                x={pos.x}
                y={pos.y}
                textAnchor={anchor as "start" | "middle" | "end"}
                dominantBaseline="middle"
                fill={labelColor}
                fontSize={7}
                fontFamily="inherit"
                fontWeight={700}
              >
                {t(`plots_descriptions.DEVICE_POSITION.${nameKey}`)}
                <tspan dx={2.5} fontWeight={400} fontSize={6} opacity={0.7}>
                  {angleLabel}
                </tspan>
              </text>
            )
          })}

          {/* Front-lean leg (vertical projection) — under the resultant */}
          <line
            x1={cx}
            y1={cy}
            x2={cx}
            y2={py}
            stroke={frontColor}
            strokeWidth={1.5}
            strokeDasharray="3 2"
            strokeLinecap="round"
            opacity={0.6}
          />
          {/* Right-lean leg (horizontal projection) — under the resultant */}
          <line
            x1={cx}
            y1={py}
            x2={px}
            y2={py}
            stroke={rightColor}
            strokeWidth={1.5}
            strokeDasharray="3 2"
            strokeLinecap="round"
            opacity={0.6}
          />

          {/* Magnitude resultant (hero) */}
          <line
            x1={cx}
            y1={cy}
            x2={px}
            y2={py}
            stroke={magnitudeColor}
            strokeWidth={2}
            strokeLinecap="round"
          />

          {/* Base reference footprint (original / 0° position) — on top of the
              magnitude line so it stays legible */}
          <rect
            x={cx - MARKER / 2}
            y={cy - MARKER / 2}
            width={MARKER}
            height={MARKER}
            fill="none"
            stroke={baseColor}
            strokeWidth={1}
            strokeDasharray="3 2"
            opacity={1}
          />

          {/* Current tower footprint at the leaned position (solid) */}
          <rect
            x={px - MARKER / 2}
            y={py - MARKER / 2}
            width={MARKER}
            height={MARKER}
            fill={currentFill}
            stroke={currentColor}
            strokeWidth={1}
          />
          {/* Center dot (origin) */}
          <circle cx={cx} cy={cy} r={2.5} fill={magnitudeColor} />
        </svg>
      </Box>

      {/* Legend */}
      {!hideLegend && (
        <Stack
          direction="row"
          spacing={2}
          justifyContent="center"
          flexWrap="wrap"
          sx={{ mt: 0.5, flexShrink: 0 }}
        >
          {legend.map(({ color, dashed, label }) => (
            <Stack key={label} direction="row" spacing={0.75} alignItems="center">
              <Box
                sx={{
                  width: 16,
                  borderTop: `2px ${dashed ? "dashed" : "solid"}`,
                  borderColor: color,
                }}
              />
              <Typography variant="caption" color="text.secondary">
                {label}
              </Typography>
            </Stack>
          ))}
        </Stack>
      )}
    </Box>
  )
}

export default TowerPositionFromAbove
